import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import AdminShell from "./AdminShell";
import GpsManagementTabs from "./GpsManagementTabs";
import { GPS_DISABLED_MESSAGE, NOT_AVAILABLE } from "./gpsManagementConstants";
import "../styles/admin.css";

export const GPS_MANAGEMENT_ERROR =
  "GPS management could not be loaded. Existing FleetX operations are unaffected.";

const EMPTY_REGISTER = {
  displayName: "", provider: "traccar", protocol: "h02", physicalIdentifier: "",
  traccarDeviceId: "", installationNotes: "",
};
const EMPTY_ASSIGNMENT = { deviceId: "", vehicleId: "", effectiveAt: "", reason: "", notes: "" };

function roleOf(user) {
  return String(user?.role || "").trim().toLowerCase().replace(/[ -]+/g, "_");
}

function formatTime(value) {
  const parsed = value ? new Date(value) : null;
  return parsed && Number.isFinite(parsed.getTime()) ? parsed.toLocaleString() : NOT_AVAILABLE;
}

function title(value) {
  const text = String(value || "").replace(/_/g, " ");
  return text ? text.replace(/\b\w/g, (letter) => letter.toUpperCase()) : NOT_AVAILABLE;
}

function vehicleLabel(vehicle) {
  const plate = String(vehicle?.plateNumber || "").trim();
  const label = String(vehicle?.label || "").trim();
  return label || plate || NOT_AVAILABLE;
}

function deviceTrackingStatus(device) {
  return device?.trackingStatus || device?.latestLocationHealth || "unknown";
}

function deviceConnectionStatus(device) {
  return device?.connectionStatus || device?.health || "unknown";
}

function safeError(error) {
  if (error?.response?.status === 404) return GPS_DISABLED_MESSAGE;
  const code = error?.response?.data?.code;
  const messages = {
    GPS_DEVICE_DUPLICATE_PROVIDER_IDENTITY: "That physical device identifier is already registered.",
    GPS_DEVICE_DUPLICATE_TRACCAR_MAPPING: "That Traccar device ID is already registered.",
    TRACKER_ALREADY_ASSIGNED: "This GPS device already has an active vehicle assignment.",
    VEHICLE_ALREADY_HAS_TRACKER: "This vehicle already has an active physical tracker.",
    GPS_DEVICE_DISABLED: "Inactive GPS devices cannot be assigned.",
    GPS_DEVICE_ACTIVE_ASSIGNMENT: "End the active assignment before disabling this device.",
    ASSIGNMENT_ALREADY_ENDED: "This assignment has already ended.",
  };
  return messages[code] || GPS_MANAGEMENT_ERROR;
}

function Modal({ title: modalTitle, children, onClose }) {
  return (
    <div className="gps-management-modal-backdrop" role="presentation">
      <section className="gps-management-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
        <header><h2>{modalTitle}</h2><button type="button" onClick={onClose} aria-label={`Close ${modalTitle}`}>×</button></header>
        {children}
      </section>
    </div>
  );
}

function Pager({ pagination, onPage }) {
  const page = pagination?.page || 1;
  const pages = Math.max(pagination?.pages || 1, 1);
  return (
    <div className="gps-management-pager">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span>Page {page} of {pages}</span>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

export default function GpsDeviceAssignmentPanel({ activeView, onViewChange }) {
  const { user } = useAuth() || {};
  const role = roleOf(user);
  const canManageDevices = role === "technical_admin";
  const canManageAssignments = ["technical_admin", "admin"].includes(role);
  const canViewHistory = canManageAssignments;
  const [devices, setDevices] = useState([]);
  const [devicePagination, setDevicePagination] = useState({ page: 1, pages: 1 });
  const [assignments, setAssignments] = useState([]);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [assignmentPagination, setAssignmentPagination] = useState({ page: 1, pages: 1 });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [deviceState, setDeviceState] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [assignmentState, setAssignmentState] = useState("active");
  const [modal, setModal] = useState(null);
  const [registerForm, setRegisterForm] = useState(EMPTY_REGISTER);
  const [editForm, setEditForm] = useState({});
  const [assignmentForm, setAssignmentForm] = useState(EMPTY_ASSIGNMENT);
  const [endReason, setEndReason] = useState("");
  const [history, setHistory] = useState([]);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const deviceSearch = activeView === "devices" ? search : "";

  const loadDevices = useCallback(async (page = 1) => {
    const params = { page, limit: 20 };
    if (deviceSearch.trim()) params.search = deviceSearch.trim();
    if (deviceState !== "all") params.state = deviceState;
    if (assignedFilter !== "all") params.assigned = assignedFilter;
    const response = await api.get("/gps/devices", { params });
    setDevices(response.data?.items || []);
    setDevicePagination(response.data?.pagination || { page, pages: 1 });
  }, [assignedFilter, deviceSearch, deviceState]);

  const loadAssignments = useCallback(async (page = 1) => {
    const params = { page, limit: 20 };
    if (assignmentState !== "all") params.state = assignmentState;
    const response = await api.get("/gps/assignments", { params });
    setAssignments(response.data?.items || []);
    setAssignmentPagination(response.data?.pagination || { page, pages: 1 });
  }, [assignmentState]);

  const loadCurrent = useCallback(async (page = 1) => {
    setLoading(true); setError("");
    try {
      if (activeView === "devices") await loadDevices(page);
      else await loadAssignments(page);
    } catch (caught) { setError(safeError(caught)); }
    finally { setLoading(false); }
  }, [activeView, loadAssignments, loadDevices]);

  useEffect(() => { loadCurrent(1); }, [loadCurrent]);

  const filteredAssignments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return assignments;
    return assignments.filter((item) => [item.tracker?.displayName, item.tracker?.maskedIdentifier,
      item.vehicle?.plateNumber, item.vehicle?.label].filter(Boolean).join(" ").toLowerCase().includes(query));
  }, [assignments, search]);

  const closeModal = () => { setModal(null); setFormError(""); setSaving(false); };
  const showSuccess = (message) => { setSuccess(message); };

  const loadOptions = async () => {
    try {
      const [deviceResult, vehicleResult] = await Promise.all([
        api.get("/gps/devices", { params: { page: 1, limit: 100, state: "enabled", assigned: "false" } }),
        api.get("/admin/vehicles", { params: { page: 1, limit: 1000, active: "active" } }),
      ]);
      setDeviceOptions(deviceResult.data?.items || []);
      setVehicles(vehicleResult.data?.vehicles || []);
    } catch (caught) { setFormError(safeError(caught)); }
  };

  const openRegister = () => { setRegisterForm(EMPTY_REGISTER); setFormError(""); setModal({ type: "register" }); };
  const openEdit = (device) => {
    setEditForm({ id: device.id, displayName: device.displayName || "", protocol: device.protocol || "h02",
      traccarDeviceId: device.traccarDeviceId || "", state: device.state || "enabled",
      providerDeviceIdentifier: "", installationNotes: "", reason: "" });
    setFormError(""); setModal({ type: "edit", device });
  };
  const openAssign = async (device = null) => {
    setAssignmentForm({ ...EMPTY_ASSIGNMENT, deviceId: device?.id || "" });
    setFormError(""); setModal({ type: "assign" }); await loadOptions();
  };

  const submitRegister = async (event) => {
    event.preventDefault();
    if (!registerForm.displayName.trim() || !registerForm.physicalIdentifier.trim()) {
      setFormError("Device label and physical identifier are required."); return;
    }
    setSaving(true); setFormError("");
    try {
      await api.post("/gps/devices", {
        provider: registerForm.provider, protocol: registerForm.protocol,
        providerDeviceIdentifier: registerForm.physicalIdentifier.trim(),
        traccarDeviceId: registerForm.traccarDeviceId ? Number(registerForm.traccarDeviceId) : null,
        displayName: registerForm.displayName.trim(), installationNotes: registerForm.installationNotes.trim(),
      });
      closeModal(); showSuccess("GPS device registered."); await loadDevices(1);
    } catch (caught) { setFormError(safeError(caught)); setSaving(false); }
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    if (!editForm.displayName?.trim() || !editForm.reason?.trim()) {
      setFormError("Device label and update reason are required."); return;
    }
    const payload = { displayName: editForm.displayName.trim(), protocol: editForm.protocol,
      traccarDeviceId: editForm.traccarDeviceId ? Number(editForm.traccarDeviceId) : null,
      state: editForm.state, reason: editForm.reason.trim() };
    if (editForm.providerDeviceIdentifier?.trim()) payload.providerDeviceIdentifier = editForm.providerDeviceIdentifier.trim();
    if (editForm.installationNotes?.trim()) payload.installationNotes = editForm.installationNotes.trim();
    setSaving(true); setFormError("");
    try {
      await api.patch(`/gps/devices/${editForm.id}`, payload);
      closeModal(); showSuccess("GPS device updated."); await loadDevices(devicePagination.page);
    } catch (caught) { setFormError(safeError(caught)); setSaving(false); }
  };

  const submitAssignment = async (event) => {
    event.preventDefault();
    if (!assignmentForm.deviceId || !assignmentForm.vehicleId || !assignmentForm.reason.trim()) {
      setFormError("GPS device, vehicle, and assignment reason are required."); return;
    }
    setSaving(true); setFormError("");
    try {
      await api.post("/gps/assignments", { ...assignmentForm,
        effectiveAt: assignmentForm.effectiveAt ? new Date(assignmentForm.effectiveAt).toISOString() : undefined });
      closeModal(); showSuccess("GPS device assigned to vehicle."); await loadCurrent(1);
    } catch (caught) { setFormError(safeError(caught)); setSaving(false); }
  };

  const submitEnd = async () => {
    if (!endReason.trim()) { setFormError("An assignment end reason is required."); return; }
    setSaving(true); setFormError("");
    try {
      await api.patch(`/gps/assignments/${modal.assignment.id}/end`, { reason: endReason.trim() });
      closeModal(); showSuccess("GPS assignment ended and retained in history."); await loadAssignments(1);
    } catch (caught) { setFormError(safeError(caught)); setSaving(false); }
  };

  const openHistory = async (assignment) => {
    setHistory([]); setFormError(""); setModal({ type: "history", assignment });
    try {
      const response = await api.get(`/gps/vehicles/${assignment.vehicle.id}/assignment-history`, { params: { page: 1, limit: 100 } });
      setHistory(response.data?.items || []);
    } catch (caught) { setFormError(safeError(caught)); }
  };

  const updateRegister = (field, value) => setRegisterForm((current) => ({ ...current, [field]: value }));
  const updateEdit = (field, value) => setEditForm((current) => ({ ...current, [field]: value }));
  const updateAssignment = (field, value) => setAssignmentForm((current) => ({ ...current, [field]: value }));

  return (
    <AdminShell pageClassName="admin-shell--live-monitoring" eyebrow="FLEET MONITORING" title="Live Tracking" subtitle="Physical GPS device and assignment administration.">
      <div className="gps-management-workspace">
        <GpsManagementTabs activeView={activeView} onViewChange={onViewChange} />
        {error ? <div className="gps-fleet-state gps-fleet-state--error" role="alert">{error}</div> : null}
        {success ? <div className="gps-management-success" role="status">{success}</div> : null}
        <div className="gps-management-toolbar">
          <input type="search" aria-label={`Search ${activeView}`} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search devices, identifiers, or vehicles..." />
          {activeView === "devices" ? <>
            <select aria-label="Device status filter" value={deviceState} onChange={(event) => setDeviceState(event.target.value)}>
              <option value="all">All device statuses</option><option value="enabled">Enabled</option><option value="disabled">Disabled</option><option value="archived">Archived</option>
            </select>
            <select aria-label="Assignment filter" value={assignedFilter} onChange={(event) => setAssignedFilter(event.target.value)}>
              <option value="all">All assignment states</option><option value="assigned">Assigned</option><option value="unassigned">Unassigned</option>
            </select>
            {canManageDevices ? <button type="button" onClick={openRegister}>Register device</button> : null}
          </> : <>
            <select aria-label="Assignment status filter" value={assignmentState} onChange={(event) => setAssignmentState(event.target.value)}>
              <option value="all">All assignment statuses</option><option value="active">Active</option><option value="ended">Ended</option>
            </select>
            {canManageAssignments ? <button type="button" onClick={() => openAssign()}>Assign device</button> : null}
          </>}
          <button type="button" onClick={() => loadCurrent(activeView === "devices" ? devicePagination.page : assignmentPagination.page)}>Refresh</button>
        </div>

        {loading ? <div className="gps-fleet-state" role="status">Loading GPS {activeView}...</div> : null}
        {!loading && activeView === "devices" ? <section className="gps-management-table-card" aria-label="GPS devices">
          <table><thead><tr><th>Device</th><th>Identities</th><th>Status</th><th>Assignment</th><th>Last GPS update</th><th>Actions</th></tr></thead>
            <tbody>{devices.length ? devices.map((device) => <tr key={device.id}>
              <td><strong>{device.displayName || NOT_AVAILABLE}</strong><small>FleetX ID: {device.id}</small><small>{title(device.classification)}</small></td>
              <td><span>{device.maskedIdentifier || NOT_AVAILABLE}</span><small>Traccar: {device.traccarDeviceId ?? NOT_AVAILABLE}</small><em>{title(device.reconciliationStatus)}</em></td>
              <td><span>{title(device.state)}</span><small>Connection: {title(deviceConnectionStatus(device))}</small><small>GPS: {title(deviceTrackingStatus(device))}</small></td>
              <td>{device.assignedVehicle ? <>
                <strong>{vehicleLabel(device.assignedVehicle)}</strong>
                <small>{device.assignedVehicle.plateNumber || NOT_AVAILABLE}</small>
                <small>{formatTime(device.currentAssignment?.assignedAt)}</small>
              </> : NOT_AVAILABLE}</td>
              <td>{formatTime(device.lastGpsUpdate || device.lastValidFixAt || device.lastConnectionAt)}</td>
              <td><div className="gps-management-actions">
                {canManageDevices ? <button type="button" onClick={() => openEdit(device)}>Edit</button> : null}
                {canManageAssignments && device.state === "enabled" && !device.hasCurrentAssignment ? <button type="button" onClick={() => openAssign(device)}>Assign</button> : null}
              </div></td>
            </tr>) : <tr><td colSpan="6">No GPS devices match the selected filters.</td></tr>}</tbody></table>
          <Pager pagination={devicePagination} onPage={loadDevices} />
        </section> : null}

        {!loading && activeView === "assignments" ? <section className="gps-management-table-card" aria-label="GPS assignments">
          <table><thead><tr><th>Device</th><th>Vehicle</th><th>State</th><th>Assigned</th><th>Ended</th><th>Actions</th></tr></thead>
            <tbody>{filteredAssignments.length ? filteredAssignments.map((assignment) => <tr key={assignment.id}>
              <td><strong>{assignment.tracker?.displayName || NOT_AVAILABLE}</strong><small>{assignment.tracker?.maskedIdentifier || NOT_AVAILABLE}</small></td>
              <td><strong>{assignment.vehicle?.plateNumber || NOT_AVAILABLE}</strong><small>{assignment.vehicle?.label || NOT_AVAILABLE}</small></td>
              <td>{title(assignment.state)}</td><td>{formatTime(assignment.assignedAt)}</td><td>{formatTime(assignment.endedAt)}</td>
              <td><div className="gps-management-actions">
                {canViewHistory ? <button type="button" onClick={() => openHistory(assignment)}>History</button> : null}
                {canManageAssignments && assignment.state === "active" ? <button type="button" onClick={() => { setEndReason(""); setFormError(""); setModal({ type: "end", assignment }); }}>End assignment</button> : null}
              </div></td>
            </tr>) : <tr><td colSpan="6">No GPS assignments match the selected filters.</td></tr>}</tbody></table>
          <Pager pagination={assignmentPagination} onPage={loadAssignments} />
        </section> : null}
      </div>

      {modal?.type === "register" ? <Modal title="Register GPS device" onClose={closeModal}><form onSubmit={submitRegister} className="gps-management-form">
        <label>Device label<input aria-label="Device label" value={registerForm.displayName} onChange={(event) => updateRegister("displayName", event.target.value)} /></label>
        <label>Physical identifier / IMEI<input aria-label="Physical identifier" value={registerForm.physicalIdentifier} onChange={(event) => updateRegister("physicalIdentifier", event.target.value)} autoComplete="off" /></label>
        <label>Traccar numeric ID (optional)<input aria-label="Traccar device ID" type="number" min="1" value={registerForm.traccarDeviceId} onChange={(event) => updateRegister("traccarDeviceId", event.target.value)} /></label>
        <label>Protocol<select aria-label="Device protocol" value={registerForm.protocol} onChange={(event) => updateRegister("protocol", event.target.value)}><option value="h02">H02</option></select></label>
        <label>Installation notes<textarea aria-label="Installation notes" value={registerForm.installationNotes} onChange={(event) => updateRegister("installationNotes", event.target.value)} /></label>
        {formError ? <div role="alert">{formError}</div> : null}<footer><button type="button" onClick={closeModal}>Cancel</button><button type="submit" disabled={saving}>Register device</button></footer>
      </form></Modal> : null}

      {modal?.type === "edit" ? <Modal title="Edit GPS device" onClose={closeModal}><form onSubmit={submitEdit} className="gps-management-form">
        <label>Device label<input aria-label="Edit device label" value={editForm.displayName || ""} onChange={(event) => updateEdit("displayName", event.target.value)} /></label>
        <label>New physical identifier (optional)<input aria-label="New physical identifier" value={editForm.providerDeviceIdentifier || ""} onChange={(event) => updateEdit("providerDeviceIdentifier", event.target.value)} autoComplete="off" /></label>
        <label>Traccar numeric ID<input aria-label="Edit Traccar device ID" type="number" min="1" value={editForm.traccarDeviceId || ""} onChange={(event) => updateEdit("traccarDeviceId", event.target.value)} /></label>
        <label>Status<select aria-label="Edit device status" value={editForm.state || "enabled"} onChange={(event) => updateEdit("state", event.target.value)}><option value="enabled">Enabled</option><option value="disabled">Disabled</option><option value="archived">Archived</option></select></label>
        <label>New installation notes (optional)<textarea aria-label="New installation notes" value={editForm.installationNotes || ""} onChange={(event) => updateEdit("installationNotes", event.target.value)} /></label>
        <label>Update reason<input aria-label="Device update reason" value={editForm.reason || ""} onChange={(event) => updateEdit("reason", event.target.value)} /></label>
        {formError ? <div role="alert">{formError}</div> : null}<footer><button type="button" onClick={closeModal}>Cancel</button><button type="submit" disabled={saving}>Save device</button></footer>
      </form></Modal> : null}

      {modal?.type === "assign" ? <Modal title="Assign GPS device" onClose={closeModal}><form onSubmit={submitAssignment} className="gps-management-form">
        <label>GPS device<select aria-label="Assignment GPS device" value={assignmentForm.deviceId} onChange={(event) => updateAssignment("deviceId", event.target.value)}><option value="">Select device</option>{deviceOptions.filter((device) => device.state === "enabled" && !device.hasCurrentAssignment).map((device) => <option key={device.id} value={device.id}>{device.displayName} — {device.maskedIdentifier}</option>)}</select></label>
        <label>Vehicle<select aria-label="Assignment vehicle" value={assignmentForm.vehicleId} onChange={(event) => updateAssignment("vehicleId", event.target.value)}><option value="">Select vehicle</option>{vehicles.map((vehicle) => <option key={vehicle._id || vehicle.id} value={vehicle._id || vehicle.id}>{vehicle.plateNo || vehicle.plateNumber} — {[vehicle.make, vehicle.model].filter(Boolean).join(" ")}</option>)}</select></label>
        <label>Effective date<input aria-label="Assignment effective date" type="datetime-local" value={assignmentForm.effectiveAt} onChange={(event) => updateAssignment("effectiveAt", event.target.value)} /></label>
        <label>Assignment reason<input aria-label="Assignment reason" value={assignmentForm.reason} onChange={(event) => updateAssignment("reason", event.target.value)} /></label>
        <label>Notes<textarea aria-label="Assignment notes" value={assignmentForm.notes} onChange={(event) => updateAssignment("notes", event.target.value)} /></label>
        {formError ? <div role="alert">{formError}</div> : null}<footer><button type="button" onClick={closeModal}>Cancel</button><button type="submit" disabled={saving}>Assign device</button></footer>
      </form></Modal> : null}

      {modal?.type === "end" ? <Modal title="End GPS assignment" onClose={closeModal}><div className="gps-management-form">
        <p>This ends the active assignment but preserves its complete history. No GPS positions will be deleted.</p>
        <label>End reason<input aria-label="Assignment end reason" value={endReason} onChange={(event) => setEndReason(event.target.value)} /></label>
        {formError ? <div role="alert">{formError}</div> : null}<footer><button type="button" onClick={closeModal}>Cancel</button><button type="button" onClick={submitEnd} disabled={saving}>Confirm end assignment</button></footer>
      </div></Modal> : null}

      {modal?.type === "history" ? <Modal title="Assignment history" onClose={closeModal}><div className="gps-management-history">
        {formError ? <div role="alert">{formError}</div> : null}
        {history.length ? history.map((item) => <article key={item.id}><strong>{item.tracker?.displayName || NOT_AVAILABLE}</strong><span>{title(item.state)}</span><small>{formatTime(item.assignedAt)} — {formatTime(item.endedAt)}</small><p>{item.assignmentReason || NOT_AVAILABLE}</p></article>) : !formError ? <p>No assignment history is available for this vehicle.</p> : null}
      </div></Modal> : null}
    </AdminShell>
  );
}
